import { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, Loader2, Image as ImageIcon, AlertTriangle, Eye, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/apiConfig';

interface KYCDocumentUploadProps {
  label: string;
  documentType: string;
  value: string;
  onChange: (url: string) => void;
  required?: boolean;
  hint?: string;
  providerId?: string;
  fieldName?: string;
  /** Bridge API action to call. Defaults to the Ambulance upload endpoint for backward compatibility. */
  action?: string;
  /** Provider session token — required by auth-protected upload actions (e.g. homecare_kyc_upload). */
  token?: string | null;
}

const KYCDocumentUpload = ({ label, documentType, value, onChange, required, hint, providerId, fieldName, action, token }: KYCDocumentUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [mismatchWarning, setMismatchWarning] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP or PDF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    setMismatchWarning(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('action', action || 'ambulance_kyc_upload');
      formData.append('document_label', label);
      if (providerId) formData.append('provider_id', providerId);
      if (fieldName) formData.append('field_name', fieldName);
      if (token) formData.append('token', token);

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (result.success && result.url) {
        onChange(result.url);
        toast.success(`${label} uploaded successfully`);
        // Advisory only — a mismatch here is a hint to double-check, not a
        // block. Vision classification can be wrong on blurry/unusual photos,
        // so the admin's manual KYC review stays the real gate.
        const c = result.classification;
        if (c?.checked && c.matches === false) {
          setMismatchWarning(
            c.detected_label && c.detected_label !== 'Other/Unclear'
              ? `This looks like a ${c.detected_label}, not ${label}. Please double-check before submitting.`
              : `Couldn't clearly confirm this is your ${label}${c.note ? ` — ${c.note}` : ''}. Please double-check.`
          );
        }
      } else {
        toast.error(result.message || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
    setMismatchWarning(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isImage = value?.startsWith('data:image/') || /\.(jpg|jpeg|png|webp)$/i.test(value || '');
  const isPdf = value?.startsWith('data:application/pdf') || /\.pdf$/i.test(value || '');

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}

      {mismatchWarning && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-xs text-amber-700">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{mismatchWarning}</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {value ? (
        <div className="relative border rounded-lg p-3 bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="shrink-0 rounded overflow-hidden border focus:outline-none focus:ring-2 focus:ring-primary"
              title={`Preview ${label}`}
            >
              {isImage ? (
                <div className="w-16 h-16">
                  <img src={value} alt={label} className="w-full h-full object-cover" />
                </div>
              ) : isPdf ? (
                <div className="w-16 h-16 bg-red-100 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-red-600" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </button>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-700 dark:text-green-400 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Document Uploaded
              </p>
              <p className="text-xs text-muted-foreground">
                {documentType} ready for verification
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setPreviewOpen(true)}
              title={`View ${label}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700"
              onClick={handleRemove}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="w-full h-20 border-dashed hover:border-primary hover:bg-primary/5 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Upload className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Click to upload {label}
              </span>
              <span className="text-xs text-muted-foreground">
                JPG, PNG, PDF (max 5MB)
              </span>
            </div>
          )}
        </Button>
      )}

      {value && (
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{label}</DialogTitle>
            </DialogHeader>
            {isImage ? (
              <img src={value} alt={label} className="max-h-[70vh] w-full rounded-lg border object-contain" />
            ) : isPdf ? (
              <div className="space-y-3">
                <iframe src={value} title={label} className="h-[70vh] w-full rounded-lg border" />
                <a
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
                </a>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Preview not available for this file type.</p>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default KYCDocumentUpload;
